<template>
  <v-card
    :variant="cardVariant"
    :class="[`sc-record-card mx-auto ${cardClass}`]"
    class="d-flex flex-column"
    :height="isListView ? '100%' : 'auto'"
  >
    <v-card-title v-if="showTitle">Deal</v-card-title>
    <v-divider v-if="showTitle" />
    <v-img
      v-if="showPhoto"
      :src="getDealPhotoUrl"
      :aspect-ratio="16 / 9"
      :min-height="isListView ? 200 : 'auto'"
      :max-height="isListView ? 200 : 'auto'"
    />
    <v-list :class="showSummary ? 'pb-0' : 'pb-4'" class="record-content">
      <v-list-item>
        <div class="pb-0">
          <span class="record-id">Deal ID# {{ itemRecord._key }}</span>
          <v-list-item-title class="headline mb-1">
            <div class="d-flex align-start">
              <router-link
                v-if="allowLink"
                :to="getAccountRoute({ name: 'deal', params: { id: itemRecord._key }})"
                class="headline mb-1"
              >
                {{ itemRecord.name }}
              </router-link>
              <span v-else>{{ itemRecord.name }}</span>
              <a @click="edit">
                <v-btn
                  v-if="isEditable"
                  size="x-small"
                  class="record-card-edit"
                  icon
                  data-track="deal.record-card.edit"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </a>
              <v-checkbox
                v-if="isListView"
                :model-value="selected"
                class="app-checkbox--record-card"
                :ripple="false"
                hide-details
                @update:model-value="$emit('click:select', item)"
              />
            </div>
          </v-list-item-title>
          <div v-if="!!itemRecord.contract_pending">
            <CommonChip class="mt-1" color="orange">Contract Pending</CommonChip>
          </div>
        </div>
      </v-list-item>
    </v-list>
    <template v-if="showSummary">
      <v-divider class="ma-4 mb-0" />
      <v-card-text class="record-card-content">
        <DealSaleCardRelatedContent v-if="isRelatedContent" :deal="itemRecord" />
        <!--   todo: move v-else into separate component       -->
        <template v-else>
          <div>
            <strong>{{ $t('transaction_type') }}: </strong>
            <span style="text-transform: capitalize">{{ getDealTransactionTypeLabel(itemRecord) }}</span>
          </div>
          <SaleConditionsLabel
            v-if="isValuePresent(itemRecord.sale_conditions)"
            :sale-conditions="itemRecord.sale_conditions"
          />
          <div>
            <strong>Property Type: </strong>
            <span v-if="propertyType" style="text-transform: capitalize">{{ propertyType }}</span>
            <span v-else class="na">N/A</span>
          </div>
          <div>
            <strong>Transaction Category: </strong>
            <Label :value="itemRecord.category" />
          </div>
          <div v-if="'source' in itemRecord">
            <strong>{{ $t(LocalizationEnum.deal_pipeline_source_category) }}: </strong>
            <span>{{ dealSource }}</span>
          </div>
          <div v-if="!!itemRecord.suite">
            <strong>Suite #: </strong>
            <span>{{ itemRecord.suite }}</span>
          </div>
          <div v-if="propertyType === PropertyTypeEnum.retail && itemRecord.property.center_type">
            <strong>{{ $t(LocalizationEnum.retail_center_type) }}: </strong>
            <span>{{ PropertyCenterTypeEnumLabels[itemRecord.property.center_type] }}</span>
          </div>
          <div v-if="isBuildingSizeInObject">
            <strong>Space Size: </strong>
            <BuildingSizeLabel :value="buildingSize" />
          </div>
          <div v-if="isLandSizeInObject">
            <strong>Land Size: </strong>
            <LandSizeLabel :value="landSize" />
          </div>
          <div v-if="isTotalConsiderationInObject">
            <strong>Total Consideration: </strong>
            <span>
              {{ $filters.formatCurrency(totalConsideration) }}
              <v-btn
                v-if="isEditable"
                icon
                class="float-right"
                data-track="deal.record-card-consideration.refresh"
                @click="refreshConsideration"
              ><v-icon size="small">mdi-refresh</v-icon></v-btn>
            </span>
          </div>
          <div v-if="isTotalCommissionInObject">
            <strong>Total Commission: </strong>
            {{ $filters.formatCurrency(totalCommission) }}
          </div>
          <div v-if="itemRecord.in_house_gross">
            <strong>In House Gross: </strong>
            <span>{{ $filters.formatCurrency(itemRecord.in_house_gross) }}</span>
          </div>
          <div v-if="contacts.length">
            <strong>{{ $t('referring') }}: </strong>
            <span v-if="referringAgentContacts.length">
              <span v-for="(contact, index) in referringAgentContacts" :key="contact._id">
                {{ contact.first_name }} {{ contact.last_name }}<span v-if="index < referringAgentContacts.length - 1">,</span>
              </span>
            </span>
            <Label v-else />
          </div>
          <div v-if="contacts.length">
            <strong>{{ $t('receiving') }}: </strong>
            <span v-if="receivingAgentContacts.length">
              <span v-for="(contact, index) in receivingAgentContacts" :key="contact._id">
                {{ contact.first_name }} {{ contact.last_name }}<span v-if="index < receivingAgentContacts.length - 1">,</span>
              </span>
            </span>
            <Label v-else />
          </div>
          <div v-if="'inbound' in itemRecord || 'outbound' in itemRecord">
            <strong>Inbound / Outbound: </strong>
            <CommonChip v-if="itemRecord.inbound" class="mb-2">
              Inbound
            </CommonChip>
            <span v-if="itemRecord.inbound && itemRecord.outbound" class="px-1" />
            <CommonChip v-if="itemRecord.outbound" class="mb-2">
              Outbound
            </CommonChip>
          </div>
          <div>
            <strong>Transaction Date: </strong>
            <Label :value="formattedTransactionDate" />
          </div>
          <div v-if="adjustments.length">
            <strong>Adjustments</strong><br>
            <div v-for="(value, index) in adjustments" :key="index">
              {{ value.type }}: {{ $filters.formatCurrency(value.amount) }}
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-icon
                    size="small"
                    class="cursor-pointer"
                    v-bind="props"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ value.memo }}</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="'internal_id' in itemRecord">
            <strong>{{ $t(LocalizationEnum.deal_internal_id) }}: </strong>
            <Label :value="itemRecord.internal_id" />
          </div>
          <div v-if="businessLine">
            <strong>{{ $t(LocalizationEnum.business_line) }}: </strong>
            {{ businessLine }}
          </div>
        </template>
        <ConfidentialChip v-if="itemRecord.confidential" class="mt-2" />
      </v-card-text>
    </template>
    <v-card-actions v-if="allowSelect" style="margin-top: auto;">
      <v-btn
        block
        class="primary"
        data-track="deal.record-card.select"
        @click="select"
      >
        Select Deal
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import { uniqBy } from 'lodash-es';

import { ServiceFactory } from '@/services/ServiceFactory';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { formatJSDate } from '@/utils/filters';
import { getSourceLabel } from '@/bundles/Pipelines/helpers';
import { getDealContactsArray } from '@/bundles/Deal/helpers';
import { PropertyTypeEnum } from '@/bundles/Property/interfaces/PropertyType';
import { PropertyCenterTypeEnumLabels } from '@/bundles/Property/enums/PropertyCenterTypeEnum';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { getDealTransactionTypeLabel } from '@/bundles/Deal/helpers/transaction/getDealTransactionLabel';
import { BusinessLineLabels } from '@/bundles/Pipelines/types';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import Label from '@/bundles/Common/components/Label.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import LandSizeLabel from '@/bundles/MeasurementUnits/components/LandSizeLabel.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';
import SaleConditionsLabel from '@/bundles/Comparables/components/labels/SaleConditionsLabel.vue';
import ConfidentialChip from '@/bundles/Comparables/components/confidential/ConfidentialChip.vue';
import DealCardRelatedContent from '@/bundles/Deal/components/cards/DealCardRelatedContent.vue';

const DealService = ServiceFactory.get('deals');

export default {
  name: 'DealCard',

  components: {
    DealSaleCardRelatedContent: DealCardRelatedContent,
    ConfidentialChip,
    SaleConditionsLabel,
    Label,
    CommonChip,
    LandSizeLabel,
    BuildingSizeLabel
  },

  mixins: [AccountLayoutMixin],

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    itemId: {
      type: [Number, String],
      default: null,
    },
    isFlat: Boolean,
    cardClass: {
      type: String,
      default: '',
    },
    allowEdit: {
      type: Boolean,
      default: true,
    },
    allowLink: Boolean,
    isListView: Boolean,
    showTitle: Boolean,
    showSummary: {
      type: Boolean,
      default: true
    },
    showPhoto: {
      type: Boolean,
      default: true,
    },
    allowSelect: Boolean,
    selected: Boolean,
    isRelatedContent: Boolean,
  },

  emits: ['click:select', 'edit', 'print', 'select', 'updateDeal'],

  setup () {
    const { addNotification } = useNotifications();
    const { isSuperAdmin } = useUserRoles();

    return { addNotification, isSuperAdmin, LocalizationEnum };
  },

  data: () => ({
    itemRecord: {},
  }),

  computed: {
    PropertyCenterTypeEnumLabels () {
      return PropertyCenterTypeEnumLabels
    },
    PropertyTypeEnum () {
      return PropertyTypeEnum
    },
    ...mapState({
      token: (state) => state.idToken,
    }),
    buildingSize: function () {
      return this.$filters.formatNumber(this.itemRecord.transaction?.total_square_feet);
    },
    cardVariant: function () {
      // Outlined unless a caller asks for a flat card.
      return this.isFlat ? 'flat' : 'outlined';
    },
    isBuildingSizeInObject: function () {
      return this.itemRecord.transaction && 'total_square_feet' in this.itemRecord.transaction;
    },
    landSize: function () {
      return this.itemRecord.transaction?.total_acres || 0;
    },
    isLandSizeInObject: function () {
      return this.itemRecord.transaction && 'total_acres' in this.itemRecord.transaction;
    },
    totalConsideration: function () {
      return this.itemRecord.transaction?.consideration || null;
    },
    isTotalConsiderationInObject: function () {
      return this.itemRecord.transaction && 'consideration' in this.itemRecord.transaction;
    },
    totalCommission: function () {
      return this.itemRecord.transaction?.total_commission || null;
    },
    isTotalCommissionInObject: function () {
      return this.itemRecord.transaction && 'total_commission' in this.itemRecord.transaction;
    },
    propertyType: function () {
      return this.itemRecord.property?.type || null;
    },
    adjustments: function () {
      return this.itemRecord.transaction?.adjustments || []
    },
    formattedTransactionDate () {
      const date = this.itemRecord.transaction?.transaction_date || this.itemRecord.transaction_date;
      return date ? formatJSDate(date) : '';
    },
    isEditable: function () {
      return (this.isSuperAdmin ||
        this.$store.state.userRoles.indexOf('financial admin') > -1) && this.allowEdit;
    },
    getDealPhotoUrl: function () {
      return this.itemRecord?.photo?.url || this.item?.comparable?.property?.photo?.url || this.$placeholderPropertyImg;
    },
    dealSource () {
      return getSourceLabel(this.itemRecord.source);
    },
    contacts () {
      return getDealContactsArray(this.itemRecord.contacts) || [];
    },
    referringAgentContacts () {
      return uniqBy(this.contacts.filter(contact => !!contact.referring_agent), '_id');
    },
    receivingAgentContacts () {
      return uniqBy(this.contacts.filter(contact => !!contact.receiving_agent), '_id');
    },
    businessLine () {
      return BusinessLineLabels[this.itemRecord.business_line] || null;
    }
  },

  watch: {
    item: {
      handler: function () {
        this.itemRecord = { ...this.item };
      },
      deep: true,
    },
    itemId: function (newValue, prevValue) {
      if (newValue !== prevValue) {
        this.getDeal()
      }
    }
  },

  created () {
    if (this.itemId) {
      this.getDeal();
    } else {
      this.itemRecord = this.item;
      this.loaded = true;
    }
  },

  methods: {
    isValuePresent,
    getDealTransactionTypeLabel,
    getDeal: async function () {
      try {
        this.loaded = false;

        const { data } = await DealService.get({
          token: this.$store.state.idToken,
          id: this.itemId,
        });

        this.itemRecord = data;
      } catch (error) {
        this.addNotification(error);
      } finally {
        this.loaded = true;
      }
    },
    edit: function () {
      this.$emit('edit');
    },
    print: function () {
      this.$emit('print');
    },
    refreshConsideration: async function () {
      try {
        const { data } = await DealService.updateConsideration({
          id: this.itemRecord._key,
          token: this.token,
        });

        this.$emit('updateDeal', data);
      } catch (error) {
        this.addNotification({ ...error });
      }
    },
    select () {
      this.$emit('select', this.item._id);
    },
  },
};
</script>

<style scoped lang="scss">
.v-card-text.record-card-content > div .v-btn {
  display: none;
}
.v-card-text.record-card-content > div:hover .v-btn {
  display: initial;
}
</style>
